import React, { useState, useEffect } from 'react'
import axios from 'axios'
import { toast } from 'sonner'
import { Tooltip } from '@material-tailwind/react'
import {
    ArrowDownTrayIcon,
    ArrowPathRoundedSquareIcon,
    TrashIcon,
} from '@heroicons/react/24/solid'
import DataTableShell from '../../../../Components/table/DataTableShell'
import TableToolbar from '../../../../Components/table/TableToolbar'
import {
    TableActionGroup,
    TableEmptyRow,
    TableIdCell,
    TableSkeletonRows,
    TableStatusChip,
} from '../../../../Components/table/TableCells'
import useClientTable from '../../../../Components/table/useClientTable'

const TABLE_HEAD = ['Request ID', 'Requested By', 'Source File', 'Brand Filters', 'Status', 'Actions']
const newestFirst = (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)

export function DataTable({ isAltereds }) {
    const [requestData, setRequestData] = useState([])
    const [isAltered, setIsAltered] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const {
        currentPage,
        setCurrentPage,
        pageItems,
        totalPages,
        totalFiltered,
    } = useClientTable({
        data: requestData,
        pageSize: 10,
        sortFn: newestFirst,
    })

    useEffect(() => {
        axios.get('/api/data-requests/all')
        .then(response => {
            if (response.data.message) {
                console.log("Cannot get batches")
            } else {
                setRequestData(response.data.requests)
                console.log(response.data.requests)
            }
        })
        .catch(error => {
            console.error('Error:', error)
        })
        .finally(() => setIsLoading(false))
    }, [isAltereds, isAltered])

    const handleDeleteRequest = (requestId) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this request?')

        if(isConfirmed){
            axios.delete(`/api/data-requests/delete/${requestId}`)
            .then(response =>{
                if(response.data.message){
                    toast.success(response.data.message)
                    setIsAltered(prevstate => !prevstate)
                }
                else{
                    toast.error(response.data.message)
                }
            })
            .catch(error =>{
                console.error('Error',error)
            })
        }

    }

    return (
        <DataTableShell
            minWidth="980px"
            toolbar={
                <TableToolbar
                    kicker="Data gathering"
                    title="Data Requests"
                    meta={`${totalFiltered} requests`}
                />
            }
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalPages={totalPages}
        >
                <thead>
                    <tr>
                        {TABLE_HEAD.map(head => <th key={head}>{head}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {isLoading ? (
                        <TableSkeletonRows columns={TABLE_HEAD.length} rows={10} />
                    ) : pageItems.length === 0 ? (
                        <TableEmptyRow
                            columns={TABLE_HEAD.length}
                            title="No data requests found"
                            description="Submitted data-gathering requests will appear here."
                        />
                    ) : pageItems.map(item => {
                        const brandFilters = item.brandFilters || []
                        const displayBrands = brandFilters.slice(0, 3)
                        const remainingBrands = brandFilters.slice(3)
                        const statusName = item.status?.name || 'error'

                        return (
                            <tr key={item.requestId}>
                                <td><TableIdCell>{item.requestId}</TableIdCell></td>
                                <td>{item.requestedBy}</td>
                                <td>{item.dataSource === 'allListings' ? 'All Listings' : 'App Eagle'}</td>
                                <td>
                                    <div className="flex flex-wrap gap-1">
                                        {displayBrands.map(filter => (
                                            <span className="nx-chip-ok" key={filter.brandName}>
                                                {filter.brandName}
                                            </span>
                                        ))}
                                        {remainingBrands.length > 0 && (
                                            <Tooltip content={
                                                <div>
                                                    {remainingBrands.map(filter => (
                                                        <div key={filter.brandName}>{filter.brandName}</div>
                                                    ))}
                                                </div>
                                            }>
                                                <span className="nx-chip-info cursor-help">
                                                    +{remainingBrands.length} more
                                                </span>
                                            </Tooltip>
                                        )}
                                    </div>
                                </td>
                                <td>
                                    <Tooltip content={item.status?.message || statusName}>
                                        <span><TableStatusChip status={statusName} /></span>
                                    </Tooltip>
                                </td>
                                <td>
                                    <TableActionGroup>
                                        {statusName === 'completed' && (
                                            <Tooltip content="Export is not available yet">
                                                <button
                                                    type="button"
                                                    className="nx-btn-ghost !px-2 opacity-40"
                                                    disabled
                                                    aria-label={`Export request ${item.requestId} unavailable`}
                                                >
                                                    <ArrowDownTrayIcon className="h-5 w-5" />
                                                </button>
                                            </Tooltip>
                                        )}
                                        {statusName !== 'open' && (
                                            <Tooltip content="Resync is not available yet">
                                                <button
                                                    type="button"
                                                    className="nx-btn-ghost !px-2 opacity-40"
                                                    disabled
                                                    aria-label={`Resync request ${item.requestId} unavailable`}
                                                >
                                                    <ArrowPathRoundedSquareIcon className="h-5 w-5" />
                                                </button>
                                            </Tooltip>
                                        )}
                                        <Tooltip content="Delete request">
                                            <button
                                                type="button"
                                                className="nx-btn-ghost !px-2 text-red-400"
                                                onClick={() => handleDeleteRequest(item.requestId)}
                                                aria-label={`Delete request ${item.requestId}`}
                                            >
                                                <TrashIcon className="h-5 w-5" />
                                            </button>
                                        </Tooltip>
                                    </TableActionGroup>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
        </DataTableShell>
    )
}